import Table from '@/pages/reports/non-ats-reports/shared/table';
import { Box, Icon, Flex, useDisclosure, Tooltip } from '@chakra-ui/react';
import { KText } from '@/ui-library/components';
import { useEffect, useMemo, useState } from 'react';
import { useFlexLayout, useTable, useExpanded, useSortBy } from 'react-table';
import { AiFillCaretDown, AiFillCaretRight } from 'react-icons/ai';
import { PassthroughValue } from '../../../../shared/passthrough-value';

import { Title } from '@/pages/reports/non-ats-reports/shared/title';
import { formatMonthString } from '@/utils/reports/chartTableFlatTransformer';
// import { logicalSort } from '@/pages/reports/non-ats-reports/shared/sort/logicalSort';
import { useKFlags } from '@/hooks/use-k-flags';
import { DrilldownActivityPopup } from '../../../drilldownActivity';
import { fetchCandidateTouchpointDrilldown } from '@/store/reports/flow-health/candidate-touchpoint/actions';
import {
  selectCurrentDrillPage,
  selectDrilldownData,
  selectDrilldownDataIsLoading,
  selectTotalCount,
  selectSearchTerm,
  selectInitialLoading,
} from '@/store/reports/flow-health/candidate-touchpoint/selectors';
import { useSelector } from 'react-redux';
import {
  applySearch,
  clearDrilldownData,
} from '@/store/reports/flow-health/candidate-touchpoint';
import { useFlowHealthDrilldownConfig } from './useFlowHealthDrilldownConfig';
import { Table as AtsTable } from '@/components/ats';
import { HeaderCell } from '@/components/ats/Table/tableCells';

// eslint-disable-next-line react-refresh/only-export-components -- pre-existing; unrelated to flag removal
export const groupBy = [
  { id: null, label: 'Time' },
  { id: 'flow', label: 'Flow' },
  { id: 'recruiter', label: 'Recruiter' },
  { id: 'company', label: 'Company' },
  { id: 'location', label: 'Location' },
  { id: 'school', label: 'School' },
  { id: 'source', label: 'Source' },
  { id: 'department', label: 'Department' },
  { id: 'job_name', label: 'Job name' },
];

const createHeaderText = (text) => <HeaderCell title={text} variant="" />;

const calcPercentage = (numerator, denominator) => {
  return numerator > 0 && denominator > 0
    ? Math.round((numerator / denominator) * 100)
    : 0;
};

const createHeaderWithTooltip = (text, tooltip) => (
  <Tooltip
    label={tooltip}
    arrowSize={8}
    hasArrow
    placement="top"
    borderRadius="4px"
  >
    <Box>{createHeaderText(text)}</Box>
  </Tooltip>
);

// Helper to create passthrough column between two metrics
const createPassthroughColumn = (
  currentKey,
  nextKey,
  calculateTotals,
  ats_enabled
) => {
  return {
    Header: () => createHeaderText(''),
    accessor: `${nextKey}_metric`,
    width: ats_enabled ? 90 : 100,
    Cell: ({ row }) => {
      const currentValue = row?.original?.[currentKey] || 0;
      const nextValue = row?.original?.[nextKey] || 0;

      if (!currentValue) return <Box>-</Box>;

      const percentage = calcPercentage(nextValue, currentValue);
      return <PassthroughValue value={percentage} row={row} />;
    },
    Footer: () => {
      const currentTotal = calculateTotals[currentKey] || 0;
      const nextTotal = calculateTotals[nextKey] || 0;

      const percentage = calcPercentage(nextTotal, currentTotal);
      return <PassthroughValue value={percentage} />;
    },
    columnConfig: 'hidden',
  };
};

// Main function to build passthrough columns
const buildPassthroughColumns = ({
  currentTableColData,
  calculateTotals,
  ats_enabled,
  contactedColumn,
  openedColumn,
  repliedColumn,
  interestedColumn,
  baseColumn,
}) => {
  const { visibility: visibilityColumns = {} } = currentTableColData ?? {};

  // Define column order with their configs
  const columnDefinitions = [
    { key: 'contacted', column: contactedColumn },
    { key: 'opened', column: openedColumn },
    { key: 'replied', column: repliedColumn },
    { key: 'interested', column: interestedColumn },
  ];

  // If visibility is empty, treat all columns as visible
  const isVisible = (key) => {
    return (
      Object.keys(visibilityColumns).length === 0 || visibilityColumns[key]
    );
  };

  // Build final column array
  const columns = [baseColumn];

  columnDefinitions.forEach((current, index) => {
    const isCurrentVisible = isVisible(current.key);

    // Find next visible column in the sequence
    let nextVisible = null;
    for (let i = index + 1; i < columnDefinitions.length; i++) {
      if (isVisible(columnDefinitions[i].key)) {
        nextVisible = columnDefinitions[i];
        break;
      }
    }

    // Always add the main column
    columns.push({
      ...current.column,
    });

    // Inject passthrough column RIGHT AFTER current column if both current and next are visible
    if (isCurrentVisible && nextVisible) {
      columns.push(
        createPassthroughColumn(
          current.key,
          nextVisible.key,
          calculateTotals,
          ats_enabled
        )
      );
    }
  });

  return columns;
};

const createOverallRateColumn = (
  metricKey,
  baseMetricKey,
  tooltipText,
  calculateTotals,
  ats_enabled
) => {
  const title = `${metricKey.charAt(0).toUpperCase()}${metricKey.slice(
    1
  )} rate`;

  return {
    Header: () => createHeaderWithTooltip(title, tooltipText),
    accessor: `${metricKey}_metric`,
    title,
    sort: false,
    width: ats_enabled ? (metricKey === 'opened' ? 120 : 130) : 150,
    ...(metricKey === 'interested' && ats_enabled ? { padding: '0 10px' } : {}),
    Cell: ({ row }) => {
      const metricValue = row?.original?.[metricKey] || 0;
      const baseValue = row?.original?.[baseMetricKey] || 0;
      const rate = calcPercentage(metricValue, baseValue);

      return (
        <KText
          size="sm"
          fontWeight={row.canExpand && row.depth === 0 ? '700' : '500'}
          textAlign={ats_enabled && 'start'}
          {...(metricKey === 'interested' ? { padding: '0 12px' } : {})}
        >
          {rate}%
        </KText>
      );
    },
    Footer: () => {
      const metricTotal = calculateTotals[metricKey] || 0;
      const baseTotal = calculateTotals[baseMetricKey] || 0;
      const rate = calcPercentage(metricTotal, baseTotal);
      return <KText size="sm-semi-bold">{rate}%</KText>;
    },
    sortType: (rowA, rowB) => {
      const rateA =
        rowA.original?.[metricKey] > 0
          ? calcPercentage(
              rowA.original?.[metricKey],
              rowA.original?.[baseMetricKey]
            )
          : 0;
      const rateB =
        rowB.original?.[metricKey] > 0
          ? calcPercentage(
              rowB.original?.[metricKey],
              rowB.original?.[baseMetricKey]
            )
          : 0;
      return rateA - rateB;
    },
    columnConfig: 'hidden',
  };
};

//  Main function to build overall rate columns
const buildOverallRateColumns = ({
  calculateTotals,
  ats_enabled,
  openedColumn,
  repliedColumn,
  interestedColumn,
  baseColumn,
  contactedColumn,
  currentTableColData,
}) => {
  const { visibility: visibilityColumns = {} } = currentTableColData ?? {};

  // If visibility is empty, treat all columns as visible
  const isVisible = (key) => {
    return (
      Object.keys(visibilityColumns).length === 0 || visibilityColumns[key]
    );
  };

  // Define metrics with their rate configurations
  const rateDefinitions = [
    {
      key: 'opened',
      column: openedColumn,
      tooltip: 'Percentage of opened messages from total sent.',
      baseMetric: 'contacted',
    },
    {
      key: 'replied',
      column: repliedColumn,
      tooltip: 'Percentage of replies from total sent.',
      baseMetric: 'contacted',
    },
    {
      key: 'interested',
      column: interestedColumn,
      tooltip: 'Percentage of interested candidates from total sent.',
      baseMetric: 'contacted',
    },
  ];

  const columns = [baseColumn, contactedColumn];

  rateDefinitions.forEach(({ key, column, tooltip, baseMetric }) => {
    // Only inject rate column if the parent metric is visible
    if (isVisible(key)) {
      columns.push(
        createOverallRateColumn(
          key,
          baseMetric,
          tooltip,
          calculateTotals,
          ats_enabled
        )
      );
    }
    // Always add main column
    columns.push({
      ...column,
    });
  });

  return columns;
};

export const FlowHealthTable = ({
  filters = {},
  data = [],
  isReadOnly = false,
  filterParams = undefined,
  hideLinks = undefined,
  isFullScreen,
  tile = {},
  isStaticReport = false,
  onSortChange = (_sortState) => {},
  onTableColDataChange = undefined,
  initialTableColData = undefined,
  skipLocalStorage = false,
  tableColStyles = {},
  isEditMode = false,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selected, setSelected] = useState(null);
  const { ats_enabled, feReportsV3 } = useKFlags();
  const onClickRow = ({ id, row = {} }) => {
    setSelected({ row, id, type: 'table' });
    onOpen();
  };
  const isDrilldownLoading = useSelector(selectDrilldownDataIsLoading);
  const drilldownData = useSelector(selectDrilldownData);
  const totalCount = useSelector(selectTotalCount);
  const currentDrillPage = useSelector(selectCurrentDrillPage);
  const searchTerm = useSelector(selectSearchTerm);
  const initialLoading = useSelector(selectInitialLoading);

  const [currentTableColData, setCurrentTableColData] =
    useState(initialTableColData);

  useEffect(() => {
    if (feReportsV3) {
      setCurrentTableColData(initialTableColData);
    }
  }, [initialTableColData, feReportsV3]);

  const handleFlowHealthTableColDataChange = (tableColData) => {
    setCurrentTableColData(tableColData);

    if (onTableColDataChange) {
      onTableColDataChange(tableColData);
    }
  };

  //need BE support

  // const sortedData = useMemo(() => {
  //   return [...data].sort((a, b) => {
  //     const valA = a[sort.key]?.title ?? a[sort.key]?.name ?? a[sort.key];
  //     const valB = b[sort.key]?.title ?? b[sort.key]?.name ?? b[sort.key];
  //     return logicalSort(valA, valB, sort.direction);
  //   });
  // }, [sort, data]);

  // Helper functions to improve readability
  const getClickableTextProps = (value, id, row) => ({
    _hover: {
      textDecoration:
        value === 0 || (isReadOnly && !isStaticReport) || hideLinks
          ? 'none'
          : 'underline',
    },
    color: ats_enabled || value === 0 ? 'secondary-700' : 'primary-500',
    cursor:
      value === 0 || (isReadOnly && !isStaticReport) || hideLinks
        ? 'default'
        : 'pointer',
    pointerEvents: value === 0 ? 'none' : 'all',
    fontWeight: row.canExpand && row.depth === 0 && !isReadOnly ? '700' : '500',
    fontSize: '14px',
    textAlign: ats_enabled ? 'start' : undefined,
    onClick: () => {
      if ((!isReadOnly && !hideLinks) || isStaticReport) {
        onClickRow({ id, row });
      }
    },
  });

  // Calculate totals for footer
  const calculateTotals = useMemo(() => {
    if (!data || data.length === 0) return {};

    // Use the first row's total values since they should be the same across all rows
    const firstRow = data[0];
    return {
      contacted: firstRow?.contacted_total || 0,
      opened: firstRow?.opened_total || 0,
      replied: firstRow?.replied_total || 0,
      interested: firstRow?.interested_total || 0,
    };
  }, [data]);

  const renderExpanderHeader = (
    getToggleAllRowsExpandedProps,
    isAllRowsExpanded
  ) => {
    const title = groupBy?.find((g) => g.id === filters?.group_by);
    return (
      <Flex>
        {filters?.break_down && (!isReadOnly || isStaticReport) && (
          <Icon
            as={isAllRowsExpanded ? AiFillCaretDown : AiFillCaretRight}
            w="16px"
            h="16px"
            {...getToggleAllRowsExpandedProps()}
            onClick={(e) => {
              e.stopPropagation();
              getToggleAllRowsExpandedProps()?.onClick?.(e);
            }}
          />
        )}
        <KText size="sm-semi-bold" textTransform="Capitalize" ml="6px">
          {title?.label || 'Time'}
        </KText>
      </Flex>
    );
  };

  const renderGroupCell = (row) => {
    const rawData = row?.original?.group_by;
    // Format month strings for display when grouping by time
    const data = !filters.group_by ? formatMonthString(rawData) : rawData;

    if (!row.canExpand && row?.depth === 0) {
      return <Title groupBy={data} filters={filters} />;
    }
    if (!row.canExpand && row?.depth === 1) {
      return <Title groupBy={data} filters={filters} pl="24px" />;
    }
    return (
      <Flex
        {...row.getToggleRowExpandedProps({
          style: {
            paddingLeft: `${row.depth * 2}rem`,
          },
        })}
      >
        {(!isReadOnly || isStaticReport) &&
          (row.isExpanded ? (
            <Icon as={AiFillCaretDown} w="16px" h="16px" />
          ) : (
            <Icon as={AiFillCaretRight} w="16px" h="16px" />
          ))}
        <Title
          groupBy={data}
          filters={filters}
          ml="6px"
          fontWeight="semiBold"
        />
      </Flex>
    );
  };

  const title = groupBy?.find((g) => g.id === filters?.group_by);

  // Generate columns based on metrics type
  const columns = useMemo(() => {
    const baseColumn = {
      id: 'header',
      accessor: 'group_by',
      title: title?.label || 'Time',
      columnConfig: 'locked',
      Header: ({ getToggleAllRowsExpandedProps, isAllRowsExpanded }) =>
        renderExpanderHeader(getToggleAllRowsExpandedProps, isAllRowsExpanded),
      sort: true,
      Cell: ({ row }) => renderGroupCell(row),
      Footer: () => <KText size="sm-semi-bold">Total</KText>,
    };

    // Shared columns for both metrics types
    const contactedColumn = {
      Header: () => createHeaderText('Sent'),
      accessor: 'contacted',
      title: 'Sent',
      width: ats_enabled
        ? filters?.metrics === 'passthrough'
          ? 70
          : 100
        : 150,
      sort: true,
      Cell: ({ row }) => (
        <KText
          {...getClickableTextProps(row?.values?.contacted, 'contacted', row)}
        >
          {row?.values?.contacted}
        </KText>
      ),
      Footer: () => (
        <KText size="sm-semi-bold">{calculateTotals.contacted || 0}</KText>
      ),
      columnConfig: true,
    };

    const openedColumn = {
      Header: () => createHeaderText('Opened'),
      accessor: 'opened',
      title: 'Opened',
      width: ats_enabled ? 100 : filters?.metrics === 'passthrough' ? 100 : 150,
      sort: true,
      Cell: ({ row }) => (
        <KText {...getClickableTextProps(row?.values?.opened, 'opened', row)}>
          {row?.values?.opened}
        </KText>
      ),
      Footer: () => (
        <KText size="sm-semi-bold">{calculateTotals.opened || 0}</KText>
      ),
      columnConfig: true,
    };

    const repliedColumn = {
      Header: () => createHeaderText('Replied'),
      accessor: 'replied',
      title: 'Replied',
      width: ats_enabled ? 100 : 150,
      sort: true,
      Cell: ({ row }) => (
        <KText {...getClickableTextProps(row?.values?.replied, 'replied', row)}>
          {row?.values?.replied}
        </KText>
      ),
      Footer: () => (
        <KText size="sm-semi-bold">{calculateTotals.replied || 0}</KText>
      ),
      columnConfig: true,
    };

    const interestedColumn = {
      Header: () => createHeaderText('Interested'),
      accessor: 'interested',
      title: 'Interested',
      width: ats_enabled ? 100 : 150,
      sort: true,
      Cell: ({ row }) => (
        <KText
          {...getClickableTextProps(row?.values?.interested, 'interested', row)}
        >
          {row?.values?.interested}
        </KText>
      ),
      Footer: () => (
        <KText size="sm-semi-bold">{calculateTotals.interested || 0}</KText>
      ),
      columnConfig: true,
    };

    if (filters?.metrics === 'passthrough') {
      return buildPassthroughColumns({
        currentTableColData,
        calculateTotals,
        ats_enabled,
        contactedColumn,
        openedColumn,
        repliedColumn,
        interestedColumn,
        baseColumn,
      });
    }

    return buildOverallRateColumns({
      calculateTotals,
      ats_enabled,
      openedColumn,
      repliedColumn,
      interestedColumn,
      baseColumn,
      contactedColumn,
      currentTableColData,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, ats_enabled, isReadOnly, hideLinks, calculateTotals]);

  const table = useTable(
    {
      columns,
      data: data,
      initialState: {
        expanded: { 0: true },
      },
    },
    useFlexLayout,
    useSortBy,
    useExpanded
  );

  const handleSortChange = (state) => {
    const { sortBy } = state;
    const currentSort = sortBy?.[0];
    onSortChange(currentSort);
  };

  return (
    <Box h={'100%'}>
      {ats_enabled ? (
        <AtsTable
          key={filters?.metrics}
          tableId="flow-health-table"
          isColumnLevelSorting
          enableColumnConfig={isEditMode}
          defaultSortBy={[{ id: 'header', desc: false }]}
          columns={columns}
          data={data}
          enableFooter={true}
          layoutScrollContainerStyles="c-data-table wide-content table-container"
          enableExpanded={true}
          getTableState={handleSortChange}
          layoutStyles={{
            borderBottom: 'none',
          }}
          onSortChange={onSortChange}
          onTableColDataChange={
            feReportsV3
              ? handleFlowHealthTableColDataChange
              : onTableColDataChange
          }
          initialTableColData={initialTableColData}
          skipLocalStorage={skipLocalStorage}
          tableColStyles={{
            ...tableColStyles,
            hideReorderColumns: feReportsV3,
          }}
          isEditMode={isEditMode}
        />
      ) : (
        <Table
          table={table}
          isReadOnly={isReadOnly}
          isFullScreen={isFullScreen}
        />
      )}

      {isOpen && (
        <DrilldownActivityPopup
          isOpen={isOpen}
          onClose={onClose}
          filters={filters}
          selected={selected}
          filterParams={filterParams}
          fetchDrilldownAction={fetchCandidateTouchpointDrilldown}
          drilldownData={drilldownData}
          isLoading={isDrilldownLoading}
          currentDrillPage={currentDrillPage}
          totalCount={totalCount}
          applySearch={applySearch}
          resetDrill={clearDrilldownData}
          searchTerm={searchTerm}
          useColumnConfig={useFlowHealthDrilldownConfig}
          initialLoading={initialLoading}
          tile={tile}
        />
      )}
    </Box>
  );
};
